import React, { ReactNode, useMemo } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import "./globals.css"
import store from "./lib/redux/store"
import AppRouter from "./AppRouter"
import { AuthProvider } from "./lib/stsAuthProvider"
import { clientConfigApi, useClientConfigQuery } from "./hooks/useClientConfig"
import { buildUserManager } from "./lib/auth/userManagerSingleton"

// queue this request before rendering even begins
store.dispatch(clientConfigApi.endpoints.clientConfig.initiate())

const muiCache = createCache({ key: 'mui', prepend: true });

const theme = createTheme({
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  },
});
function Root() {
  return (
    <CacheProvider value={muiCache}>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <StoreRequiredProviders>
            <AppRouter />
          </StoreRequiredProviders>
        </Provider>
      </ThemeProvider>
    </CacheProvider>
  )
}

function StoreRequiredProviders({ children }: { children: ReactNode }) {
  const clientConfig = useClientConfigQuery()
  const userManager = useMemo(
    () => buildUserManager(clientConfig.data),
    [clientConfig.data])

  return (
    <AuthProvider
      signinMode={"silent"}
      userManager={userManager}
      callbackUrl="/signin-oidc"
    >
      {children}
    </AuthProvider>
  )
}

console.log('App starting...')

const container = document.getElementById('root')
if (!container) {
  console.error('Root element not found!')
} else {
  console.log('Root element found, creating React root...')
  const root = createRoot(container)
  try {
    root.render(<Root />)
    console.log('React app rendered successfully')
  } catch (error) {
    console.error('Error rendering React app:', error)
  }
}
